import { useEffect, useMemo, useRef, useState } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { ReviewsExploreHeader } from 'Deer/components/Reviews/ReviewsExploreHeader';
import { L3_PATHS } from 'Deer/constants/routes';
import useAsync from 'components/hooks/useAsync';
import { useQuery } from 'components/hooks/useQuery';
import { useHistory, useLocation } from 'react-router-dom';
import { checkNewNavEnabled } from 'utils/helpers/featureFlags';
import {
  checkFeedbackModuleEnabled,
  checkGoalModuleEnabled,
  checkLevelUpModuleEnabled,
} from 'utils/helpers/moduleAccess';
import { checkUserIsReviewAdmin } from 'utils/helpers/rolesAccess';

import { Download, NeedAttention, Plus } from 'assets/assetsV1/iconComponents';
import addReviewV1 from 'assets/assetsV1/illustrations/newIllustrations/addReviewV1.svg';
import noReviewsPendingv1 from 'assets/assetsV1/illustrations/newIllustrations/noReviewsPendingv1.svg';

import { getMatchPathIndexFromRoutesArray, goToReviewCycle } from 'constants/helper';
import { isMobileView } from 'constants/isMobileView';
import {
  sendDownloadReviewCSVEvent,
  sendSelectedReviewCycleEvent,
  sendViewReviewListEvent,
} from 'constants/mixpanelHelpers/reviewsEvents';
import { isNetchexLogin } from 'constants/netChexConstants';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';
import { routes } from 'constants/routes';

import { getCompetencySettings } from 'services/competencies';
import { getReviewHelpTimeline } from 'services/performanceReview';

import { Button } from 'uiToolkitV1/Button';
import { ChipSelect } from 'uiToolkitV1/ChipSelect';
import { EmptyScreen } from 'uiToolkitV1/EmptyScreen';
import { PageTabbedNavigation } from 'uiToolkitV1/PageTabbedNavigation';
import { Paper, PaperWithShadow } from 'uiToolkitV1/Paper';
import { ReviewTimeline } from 'uiToolkitV1/ReviewTimeline';
import { SelectDashed } from 'uiToolkitV1/Select';
import { Tooltip } from 'uiToolkitV1/Tooltip';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

import { showLoader } from '../components/common';
import PerformanceReviewGraphV1 from './PerformanceReviewGraphV1';
import ReviewsCompletedByMe from './ReviewsCompletedByMe';
import ReviewsPast from './ReviewsPast';
import ReviewsPendingOnMe from './ReviewsPendingOnMe';
import ReviewsPendingOnOthers from './ReviewsPendingOnOthers';
import useReviewSectionsData from './hooks/dataHooks';

// todo: vikalp add any logic for permissions and access
const DEFAULT_LANDING_PAGE_TABS = [
  {
    name: 'Explore',
    route: routes.performanceReview.audit,
    accessFn: () => true,
  },
];

const PREVIEW_PERFORMANCE_TAB = {
  name: 'Preview Performance',
  route: routes.performanceReview.performancePreview,
  accessFn: () => true,
};

export const REVIEW_TABLE_TYPES = {
  PENDING_ON_ME: 'PENDING_ON_ME',
  PENDING_ON_OTHERS: 'PENDING_ON_OTHERS',
  COMPLETED_BY_ME: 'COMPLETED_BY_ME',
  MY_PAST_REVIEWS: 'MY_PAST_REVIEWS',
};

const TABLE_TYPES_COMP = {
  [REVIEW_TABLE_TYPES.PENDING_ON_ME]: ReviewsPendingOnMe,
  [REVIEW_TABLE_TYPES.PENDING_ON_OTHERS]: ReviewsPendingOnOthers,
  [REVIEW_TABLE_TYPES.COMPLETED_BY_ME]: ReviewsCompletedByMe,
  [REVIEW_TABLE_TYPES.MY_PAST_REVIEWS]: ReviewsPast,
};

const TABLE_TYPES_CSV_FILENAME = {
  [REVIEW_TABLE_TYPES.PENDING_ON_ME]: 'Pending_on_me',
  [REVIEW_TABLE_TYPES.PENDING_ON_OTHERS]: 'Pending_on_others',
  [REVIEW_TABLE_TYPES.COMPLETED_BY_ME]: 'Complete_by_me',
  [REVIEW_TABLE_TYPES.MY_PAST_REVIEWS]: 'My_past_reviews',
};

const TABLE_TYPES_EVENT_VALUE_NAME = {
  [REVIEW_TABLE_TYPES.PENDING_ON_ME]: 'PendingOnMe',
  [REVIEW_TABLE_TYPES.PENDING_ON_OTHERS]: 'PendingOnOthers',
  [REVIEW_TABLE_TYPES.COMPLETED_BY_ME]: 'CompletedByMe',
  [REVIEW_TABLE_TYPES.MY_PAST_REVIEWS]: 'PastReviews',
};

const DOWNLOAD_TOOLTIP_TEXT = {
  [REVIEW_TABLE_TYPES.PENDING_ON_ME]: 'This will download all the actions/reviews pending on you across review cycles',
  [REVIEW_TABLE_TYPES.PENDING_ON_OTHERS]: 'This will download all the reviews pending on others across review cycles',
  [REVIEW_TABLE_TYPES.COMPLETED_BY_ME]: 'This will download all the reviews completed by you across review cycles',
  [REVIEW_TABLE_TYPES.MY_PAST_REVIEWS]: 'This will download information around all your past reviews',
};

const ReviewSectionSelection = ({ reviewsSections, tableChangeHandler, selectedTableId, selectedTableRef }) => {
  const TABLE_SELECT_OPTIONS = useMemo(() => {
    // Create the options array containing only the "Pending on Me" chip for mobile view
    // if (isMobileView()) {
    //   const pendingOnMeOption = {
    //     text: 'Pending on Me',
    //     id: REVIEW_TABLE_TYPES.PENDING_ON_ME,
    //   };
    //   return [pendingOnMeOption];
    // }

    return reviewsSections.map(({ sectionKeyName, sectionId, sectionData }) => {
      const { pastReviewCycleCount, reviewFormsCount } = sectionData;
      return {
        text: `${sectionKeyName} (${
          sectionId === REVIEW_TABLE_TYPES.MY_PAST_REVIEWS ? pastReviewCycleCount : reviewFormsCount
        })`,
        id: sectionId,
      };
    });
  }, [reviewsSections]);

  // if (isMobileView()) {
  //   return (
  //     <Flex alignItems="center" mb={2} py={3} px={1}>
  //       <TextBodyMedium bold color="text.g3" mr={2}>
  //         Showing :{' '}
  //       </TextBodyMedium>
  //       <ChipSelect
  //         options={TABLE_SELECT_OPTIONS}
  //         onChange={(tableId) => tableChangeHandler(tableId[0])}
  //         forceMinOneSelection
  //         selectedIds={[selectedTableId]}
  //       />
  //     </Flex>
  //   );
  // }

  return (
    <Flex alignItems="center" justify="space-between" mb={2} py={3} px={1}>
      <Flex alignItems="center">
        <TextBodyMedium bold color="text.g3" mr={2} flexShrink={0}>
          Showing :{' '}
        </TextBodyMedium>
        <ChipSelect
          options={TABLE_SELECT_OPTIONS}
          onChange={(tableId) => tableChangeHandler(tableId[0])}
          forceMinOneSelection
          selectedIds={[selectedTableId]}
        />
      </Flex>
      {!isMobileView() && (
        <Tooltip label={DOWNLOAD_TOOLTIP_TEXT[selectedTableId]} placement="bottom-end">
          <Box>
            <Button
              variant="tertiary"
              StartIcon={Download}
              onClick={() => {
                sendDownloadReviewCSVEvent({
                  eventProperties: {
                    type: 'Explore',
                    activeTab: TABLE_TYPES_EVENT_VALUE_NAME[selectedTableId],
                    source: 'reviews',
                  },
                });
                selectedTableRef.current?.api.exportDataAsCsv();
              }}
            />
          </Box>
        </Tooltip>
      )}
    </Flex>
  );
};

const ReviewSelection = ({
  setSelectedReviewCycleId,
  selectedReviewCycleId,
  selectedReviewSectionData,
  selectedTableId,
}) => {
  const reviewCycleOptions = selectedReviewSectionData.reviewCycles.map(({ id, name, formsCount }) => ({
    value: id,
    label: `${name} (${formsCount})`,
    name,
  }));

  const selectedReviewCycleValue = reviewCycleOptions.filter(({ value }) => value === selectedReviewCycleId)[0];

  const reviewSelectionHandler = (option) => {
    sendSelectedReviewCycleEvent({
      eventProperties: {
        cycleName: option.name,
        cycleId: option.value,
        source: TABLE_TYPES_EVENT_VALUE_NAME[selectedTableId],
      },
    });

    setSelectedReviewCycleId(option.value);
  };

  return (
    <Flex mb={6} py="2px" px="1">
      <Flex alignItems="center" flexGrow={1}>
        <TextBodyMedium bold color="text.g3" minWidth="100px">
          Review cycle :{' '}
        </TextBodyMedium>
        <Box ml={1} flexGrow={1}>
          <SelectDashed
            value={selectedReviewCycleValue}
            options={reviewCycleOptions}
            handleChange={reviewSelectionHandler}
            boldText
          />
        </Box>
      </Flex>
    </Flex>
  );
};

const ReviewInstanceTimeline = ({ reviewTimelineData }) => {
  if (!reviewTimelineData || reviewTimelineData?.length === 0) return null;

  return (
    <Flex mb={3} width="100%">
      <ReviewTimeline timelineData={reviewTimelineData} />
    </Flex>
  );
};

const getReviewLandingPageRoute = () =>
  checkNewNavEnabled() ? L3_PATHS['my-data'].review.myReviews : routes.performanceReview.audit;

export const getSelectedReviewSectionData = ({ reviewsSections, selectedTableId }) =>
  reviewsSections.filter(({ sectionId }) => sectionId === selectedTableId)?.[0]?.sectionData ?? null;

const getRouteHelper = ({ activeTableId, selectedReviewCycleId }) =>
  `${getReviewLandingPageRoute()}?activeTableId=${activeTableId}&reviewCycleId=${selectedReviewCycleId}`;

const testReviewCycleAndInstanceId = ({ reviewCycleId, reviewSectionData }) => {
  if (reviewCycleId === null || Number.isNaN(reviewCycleId)) return true;
  const matchedReviewCycle = reviewSectionData?.reviewCycles?.filter(({ id }) => id === reviewCycleId)[0];
  if (!matchedReviewCycle) {
    return false;
  }
  return true;
};

const checkForReviewCycleExistenceForPastReviews = (sectionData = []) => {
  const derivedSectionData = sectionData ?? [];

  return (
    (derivedSectionData ?? []).filter(({ sectionId }) => sectionId === REVIEW_TABLE_TYPES.MY_PAST_REVIEWS)?.[0]
      ?.sectionData?.pastReviewCycleCount ?? 0 >= 1
  );
};

const checkForReviewCycleExistence = (sectionData = []) => {
  const derivedSectionData = sectionData ?? [];

  // this checks for first three sections
  const count = derivedSectionData.reduce((acc, curr) => acc + (curr?.sectionData?.reviewCycles?.length ?? 0), 0);

  if (count) return !!count;
  // else check separately for past review as it has different structure
  return checkForReviewCycleExistenceForPastReviews(derivedSectionData);
};

const NoDataComp = ({ heading, description, illustrationSrc, CTAComp }) => (
  <Paper px={6} py={2}>
    <div style={{ marginTop: '60px' }}>
      <EmptyScreen heading={heading} description={description} illustrationSrc={illustrationSrc} CTAComp={CTAComp} />
    </div>
  </Paper>
);

const ReviewTables = () => {
  const { activeTableId = '', reviewCycleId = '' } = useQuery();
  let selectedReviewCycleId;
  if (reviewCycleId === '') {
    selectedReviewCycleId = reviewCycleId;
  } else {
    selectedReviewCycleId = reviewCycleId === 'null' ? null : Number(reviewCycleId);
  }
  const selectedTableId = activeTableId !== '' ? activeTableId : null;
  const queryClient = useQueryClient();
  const selectedTableRef = useRef();
  const history = useHistory();

  const setSelectedReviewCycleId = (reviewCycleId_) => {
    history.push(
      getRouteHelper({
        activeTableId,
        selectedReviewCycleId: reviewCycleId_,
      })
    );
  };

  const { data: reviewsSectionsData, isLoading: isLoadingReviewSectionsData } = useReviewSectionsData();

  const { data: reviewTimelineData, run: timelineRun } = useAsync();

  const hasReviewCycles = checkForReviewCycleExistence(reviewsSectionsData);
  const isSelectedSectionHasReviewCycle =
    selectedTableId === REVIEW_TABLE_TYPES.MY_PAST_REVIEWS
      ? checkForReviewCycleExistenceForPastReviews(reviewsSectionsData)
      : ((reviewsSectionsData ?? []).filter(({ sectionId }) => sectionId === selectedTableId)?.[0]?.sectionData
          ?.reviewCycles?.length ?? 0 >= 1);

  const reviewsSections = useMemo(() => reviewsSectionsData ?? [], [reviewsSectionsData]);
  //
  const TableComp = TABLE_TYPES_COMP?.[selectedTableId] ?? (() => null);
  const selectedReviewSectionData = getSelectedReviewSectionData({
    reviewsSections,
    selectedTableId,
  });
  const selectedReviewCycleData = selectedReviewSectionData?.reviewCycles?.filter(
    ({ id }) => id === selectedReviewCycleId
  )[0];
  const { latestFeedbackFormId, lastReviewCycleName } = selectedReviewCycleData ?? {};

  useEffect(() => {
    if (selectedTableId === REVIEW_TABLE_TYPES.MY_PAST_REVIEWS || selectedReviewCycleData?.reviewType === 'TRIGGERED') {
      return;
    }

    if (latestFeedbackFormId) {
      timelineRun(getReviewHelpTimeline({ reviewId: latestFeedbackFormId }));
    }
  }, [timelineRun, selectedTableId, selectedReviewCycleData, latestFeedbackFormId]);

  const tableChangeHandler = (newSelectedTableId) => {
    sendViewReviewListEvent({
      eventProperties: {
        type: TABLE_TYPES_EVENT_VALUE_NAME[newSelectedTableId],
      },
    });
    history.push(
      getRouteHelper({
        activeTableId: newSelectedTableId,
        selectedReviewCycleId,
      })
    );
    // to update the table to get latest data
    queryClient.invalidateQueries([REACT_QUERY_KEYS.ReviewSections]);
  };

  useEffect(() => {
    if (selectedTableId === REVIEW_TABLE_TYPES.MY_PAST_REVIEWS) {
      return;
    }

    if (reviewsSections.length > 0) {
      const isValidTableReviewCycleCombination =
        selectedTableId === REVIEW_TABLE_TYPES.MY_PAST_REVIEWS ||
        testReviewCycleAndInstanceId({
          reviewCycleId: selectedReviewCycleId,
          reviewSectionData: selectedReviewSectionData,
        });

      if (!selectedTableId || !isValidTableReviewCycleCombination) {
        const firstReviewCycleData = getSelectedReviewSectionData({
          reviewsSections,
          selectedTableId: selectedTableId || reviewsSections[0].sectionId,
        }).reviewCycles[0];

        history.replace(
          getRouteHelper({
            activeTableId: selectedTableId || reviewsSections[0]?.sectionId,
            selectedReviewCycleId: firstReviewCycleData?.id,
          })
        );
      }
    }
  }, [history, reviewsSections, selectedTableId]);

  // check if the review cycle id and instance id exist in specified table or not
  const isValidTableReviewCycleCombination =
    selectedTableId === REVIEW_TABLE_TYPES.MY_PAST_REVIEWS ||
    testReviewCycleAndInstanceId({
      reviewCycleId: selectedReviewCycleId,
      reviewSectionData: selectedReviewSectionData,
    });

  const createNewReviewCycle = () => {
    goToReviewCycle({ history, entityId: 'new' });
  };

  const addReviewCycle = () => (
    <Button onClick={createNewReviewCycle} StartIcon={Plus}>
      Create New Cycle
    </Button>
  );

  if (isLoadingReviewSectionsData) {
    return (
      <PaperWithShadow px={6} py={2}>
        {showLoader()}
      </PaperWithShadow>
    );
  }
  if (!hasReviewCycles) {
    return (
      <PaperWithShadow p={3}>
        <div style={{ marginTop: '60px' }}>
          {checkUserIsReviewAdmin() ? (
            <EmptyScreen
              heading="You will find updates here once you launch a performance review cycle."
              illustrationSrc={addReviewV1}
              CTAComp={addReviewCycle}
            />
          ) : (
            <EmptyScreen
              heading="You will find updates here once your admin launches a performance review cycle."
              illustrationSrc={addReviewV1}
            />
          )}
        </div>
      </PaperWithShadow>
    );
  }

  return (
    <PaperWithShadow p={3}>
      {!isNetchexLogin && reviewsSections?.length > 0 && selectedTableId && (
        <ReviewSectionSelection
          selectedTableRef={selectedTableRef}
          reviewsSections={reviewsSections}
          tableChangeHandler={tableChangeHandler}
          selectedTableId={selectedTableId}
        />
      )}

      {!isSelectedSectionHasReviewCycle &&
        selectedTableId === 'PENDING_ON_ME' &&
        (checkUserIsReviewAdmin() ? (
          <EmptyScreen
            illustrationSrc={addReviewV1}
            heading="Clean slate ahead!"
            description="There are no pending reviews for you. Swing by later when new cycles kick off, or there are fresh reviews to fill."
            CTAComp={addReviewCycle}
          />
        ) : (
          <NoDataComp
            illustrationSrc={noReviewsPendingv1}
            heading="Clean slate ahead!"
            description="There are no pending reviews for you. Swing by later when new cycles kick off, or there are fresh reviews to fill."
          />
        ))}
      {!isSelectedSectionHasReviewCycle &&
        selectedTableId === 'PENDING_ON_OTHERS' &&
        (checkUserIsReviewAdmin() ? (
          <EmptyScreen
            illustrationSrc={addReviewV1}
            heading="Clean slate ahead!"
            description="Your colleagues don't have any pending reviews for you. Stop by later when new cycles begin, or there are new reviews to complete."
            CTAComp={addReviewCycle}
          />
        ) : (
          <NoDataComp
            illustrationSrc={noReviewsPendingv1}
            heading="Clean slate ahead!"
            description="Your colleagues don't have any pending reviews for you. Stop by later when new cycles begin, or there are new reviews to complete."
          />
        ))}
      {!isSelectedSectionHasReviewCycle && selectedTableId === 'COMPLETED_BY_ME' && (
        <EmptyScreen
          heading="Poof! Nothing here."
          illustrationSrc={noReviewsPendingv1}
          description="You can track the status of all the completed reviews across review cycles here."
        />
      )}
      {!isSelectedSectionHasReviewCycle && selectedTableId === 'MY_PAST_REVIEWS' && (
        <EmptyScreen
          heading="Poof! Nothing here."
          illustrationSrc={noReviewsPendingv1}
          description="You can track the status of all the past reviews across review cycles here."
        />
      )}
      {isValidTableReviewCycleCombination && Boolean(isSelectedSectionHasReviewCycle) && (
        <>
          {selectedReviewSectionData &&
            selectedTableId &&
            selectedTableId !== REVIEW_TABLE_TYPES.MY_PAST_REVIEWS &&
            selectedReviewCycleId !== '' && (
              <ReviewSelection
                setSelectedReviewCycleId={setSelectedReviewCycleId}
                selectedReviewCycleId={selectedReviewCycleId}
                selectedReviewSectionData={selectedReviewSectionData}
                selectedTableId={selectedTableId}
              />
            )}
          {selectedReviewCycleId !== '' &&
            selectedReviewCycleData?.reviewType !== 'TRIGGERED' &&
            !isMobileView() &&
            selectedTableId &&
            selectedTableId !== REVIEW_TABLE_TYPES.MY_PAST_REVIEWS && (
              <ReviewInstanceTimeline reviewTimelineData={reviewTimelineData} />
            )}
          {lastReviewCycleName && !isMobileView() && (
            <Flex sx={{ mt: -2, mb: 4 }} alignItems="center">
              <NeedAttention color="text.g2" boxSize="13px" />
              <TextLabel sx={{ ml: 2 }}>Displaying the timeline of the review cycle: {lastReviewCycleName}</TextLabel>
            </Flex>
          )}
          <Box minH="200px">
            <TableComp
              selectedTableRef={selectedTableRef}
              selectedTableId={selectedTableId}
              selectedReviewCycleId={selectedReviewCycleId}
            />
          </Box>
        </>
      )}
    </PaperWithShadow>
  );
};

const ReviewAudit = () => {
  const location = useLocation();
  const routesArray = DEFAULT_LANDING_PAGE_TABS.map((tabData) => tabData.route);
  const [includePreviewPerformanceTab, setIncludePreviewPerformanceTab] = useState(false);
  const { run } = useAsync();

  useEffect(() => {
    run(
      getCompetencySettings().then(({ viewType }) => {
        const isIncludePerformancePreviewTab = !(
          (viewType === 'DISABLED' || (!checkFeedbackModuleEnabled() && !checkLevelUpModuleEnabled())) &&
          !checkGoalModuleEnabled()
        );
        setIncludePreviewPerformanceTab(isIncludePerformancePreviewTab);
      })
    );
  }, [run]);

  const selectedTabRouteIndex = getMatchPathIndexFromRoutesArray({
    pathname: location.pathname,
    routesArray,
  });

  const selectedTabName = [...DEFAULT_LANDING_PAGE_TABS, PREVIEW_PERFORMANCE_TAB]?.[selectedTabRouteIndex]?.name ?? '';

  const visibleTabs = includePreviewPerformanceTab
    ? [...DEFAULT_LANDING_PAGE_TABS, PREVIEW_PERFORMANCE_TAB]
    : DEFAULT_LANDING_PAGE_TABS;
  return (
    <Box>
      {!checkNewNavEnabled() && (
        <PageTabbedNavigation tabsData={visibleTabs} selectedTabName={selectedTabName} title="My Reviews" />
      )}
      {checkNewNavEnabled() && <ReviewsExploreHeader />}

      <Box p={checkNewNavEnabled() ? 0 : 4}>
        {!isMobileView() && (
          <Flex mb={3} gap={4} h="348px">
            <PaperWithShadow w="100%" py={0} h="100%">
              <PerformanceReviewGraphV1 emptyMessage="During active review cycles, you can view your performance growth curve here." />
            </PaperWithShadow>
          </Flex>
        )}

        <ReviewTables />
      </Box>
    </Box>
  );
};

export { ReviewAudit };
